import { request, UnauthorizedError } from "utils/request"
import { asyncAction } from "./common"
import { MemberRoles } from "."
import { getState } from "storeAccess"

export const loggedOutState = {
  companyId: null,
  companyName: null,
  teamId: null,
  memberId: null,
  accessToken: null,
  role: null,
  isLoggedIn: false,
  isOnlyLibrarian: null,
  isMember: true, // Default true, just to be safe
  isViewOnly: false,
  globalMessage: "",
  showGlobalMessage: false,
  hasApiToken: null,
  language: null,
  plannertechLanguage: "en",
  defaultChatbot: "",
  pendingMemberships: null,
}

function loginSuccessState(res) {
  return {
    companyId: res.company && res.company.id,
    companyName: res.company && res.company.name,
    teamId: !res.team ? res.team : res.team.id,
    memberId: res.id,
    accessToken: res.access_token,
    role: res.role,
    isOnlyLibrarian: res.isOnlyLibrarian,
    isLoggedIn: true,
    isMember: res.role === MemberRoles.MEMBER,
    isViewOnly: res.role === MemberRoles.VIEW_ONLY,
    globalMessage: res.globalMessage,
    showGlobalMessage: res.globalMessage ? true : false,
    hasApiToken: res.hasApiToken !== undefined ? res.hasApiToken : null,
    language: res.language,
    plannertechLanguage: res.plannertechLanguage ?? "en",
    defaultChatbot: res.defaultChatbotId,
    pendingMemberships: null,
  }
}

export function reducer(state = loggedOutState, action) {
  // A bit out of the ordinary reducer-stuff:
  // Log the user out if we receive such a status from the BE.
  // This works regardless of whether the user is currently logged in or not!
  if (action.err instanceof UnauthorizedError) {
    return loggedOutState
  }

  const res = action.res
  switch (action.type) {
    case "loading/fetchAll/ERROR":
      // Log out if fetching everything failed
      // This is done to prevent infinite loops!
      // But don't logout if it's an AbortError (page refresh)
      if (action.err && action.err.name === 'AbortError') {
        return state
      }
      return loggedOutState
    case "auth/login":
      if (res && res.requires_team_selection) {
        return { ...loggedOutState, pendingMemberships: res.memberships }
      }
      return loginSuccessState(res)
    case "auth/verifyMfaToken":
    case "auth/logout-other-device":
    case "auth/selectTeam":
      if (res && res.requires_team_selection) {
        return { ...loggedOutState, pendingMemberships: res.memberships }
      }
      return loginSuccessState(res)
    case "auth/accessCompany":
      return { ...state, companyId: res.id, companyName: res.name }
    case "auth/changeShowGlobalMessage":
      return { ...state, showGlobalMessage: false }
    case "auth/exitCompany":
      return { ...state, companyId: null }
    case "auth/clearPendingMemberships":
      return loggedOutState
    case "auth/logout":
      return loggedOutState
    case "auth/setGlobalMessage":
      return { ...state, globalMessage: action.args[0] }
    case "auth/setHasAPIToken":
      return { ...state, hasApiToken: action.hasApiToken }
    case "auth/setLanguage":
      return { ...state, language: action.args[0] }
    case "auth/setPlannertechLanguage":
      return { ...state, plannertechLanguage: action.plannertechLanguage }
    case "auth/setDefaultChatbot":
      return { ...state, defaultChatbot: action.args[0] }
    default:
      return state
  }
}

export const login = asyncAction("auth/login", (dispatch, email, password) => {
  return request("POST", `/login`, { email, password }, true, true)
})

export const selectTeam = asyncAction("auth/selectTeam", (dispatch, email, password, memberId) => {
  return request("POST", `/login/select-team`, { email, password, memberId }, true, true)
})

export const logout = asyncAction("auth/logout", (_dispatch) => {
  const state = getState()
  window.localStorage.setItem("plannertechVersion", JSON.stringify(state.teamStatus.plannertechVersion))
  return request("POST", `/logout`).then(() => {
    return { type: "auth/logout" }
  })
})

export const logoutOtherDevice = asyncAction("auth/logout-other-device", (dispatch, email, password) => {
  return request("POST", `/logout-other-device`, { email, password }, true, true)
})

export const accessCompany = asyncAction("auth/accessCompany", (dispatch, id, name) => {
  return request("PATCH", `/customer-admin/give-company-id/${id}`).then(() => ({ id, name }))
})

export const exitTeam = asyncAction("auth/exitCompany", (_dispatch) => {
  return request("PATCH", `/customer-admin/remove-company-id`)
})

export const getMfaToken = asyncAction("auth/getMfaToken", (dispatch, email, password) => {
  return request("POST", `/get-mfa-key`, { email, password })
})

export const verifyMfaToken = asyncAction("auth/verifyMfaToken", (dispatch, email, password, mfaKey) => {
  return request("POST", `/verify-mfa-key`, { email, password, mfaKey }, true, true)
})

export function clearPendingMemberships() {
  return { type: "auth/clearPendingMemberships" }
}

export function changeShowGlobalMessage() {
  return { type: "auth/changeShowGlobalMessage" }
}

export const setGlobalMessage = asyncAction("auth/setGlobalMessage", (dispatch, message) => {
  return request("POST", `/customer-admin/set-global-message`, { message })
})

export function setHasAPIToken(hasApiToken) {
  return { type: "auth/setHasAPIToken", hasApiToken }
}

export const setLanguage = asyncAction("auth/setLanguage", (dispatch, language) => {
  return request("PATCH", `/librarian/speech-to-text/language`, { input: { language: language } })
})

export function setPlannertechLanguage(lang) {
  return { type: "auth/setPlannertechLanguage", plannertechLanguage: lang }
}

export const setDefaultChatbot = asyncAction("auth/setDefaultChatbot", (dispatch, defaultChatbotId) => {
  return request("PATCH", `/librarian/chatbot/default-chatbot`, { patch: { defaultChatbotId } })
})
